import { useState, useEffect } from "react";
import Step0Branding from "./Steps/Step0branding";
import Step1Tags from "./Steps/Step1Tags";
import Step2Download from "./Steps/Step2Download";
import Step3Upload from "./Steps/Step3Upload";
import Step4ChooseSpot from "./Steps/Step4ChooseSpot";
import Step5Confirm from "./Steps/Step5Confirm";
import { supabase } from "../../supabaseClient";
import "./VendorOnboarding.css";

const TOTAL_STEPS = 6;
const STEP_LABELS = ["Branding", "Add-Ons", "Download", "Upload", "Spot", "Confirm"];

export default function VendorOnboarding({ vendor, event, app, onComplete }) {
  const [step, setStep] = useState(0);

  const [formData, setFormData] = useState({
    tags: [],
    extras: {},
    uploads: {},
    spot: app?.chosen_spot ?? null,
    chooseSpotUpgrade: false,
    agreed: false,
    tent_type: app?.tent_type ?? null,
  });

  const [canProceed, setCanProceed] = useState(false);

  // tent_type comes straight from the app record passed in — no need to
  // refetch vendor/user or hardcode an event id here anymore. This only
  // covers the case where app.tent_type wasn't set yet when we mounted.
  useEffect(() => {
    if (!formData.tent_type && vendor?.id && event?.id) {
      const fetchTentType = async () => {
        const { data: freshApp } = await supabase
          .from("vendor_applications")
          .select("tent_type")
          .eq("vendor_id", vendor.id)
          .eq("event_id", event.id)
          .maybeSingle();

        if (freshApp?.tent_type) {
          setFormData((prev) => ({ ...prev, tent_type: freshApp.tent_type }));
        }
      };

      fetchTentType();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendor?.id, event?.id]);

  const nextStep = () => setStep((s) => Math.min(s + 1, TOTAL_STEPS - 1));
  const prevStep = () => setStep((s) => Math.max(s - 1, 0));

  useEffect(() => {
    switch (step) {
      case 0:
        break;
      case 1:
        break;
      case 2:
        setCanProceed(true);
        break;
      case 3:
        setCanProceed(
          !!(formData.uploads?.indemnity && formData.uploads?.agreement)
        );
        break;
      case 4:
        setCanProceed(true);
        break;
      case 5:
        setCanProceed(true);
        break;
      default:
        setCanProceed(false);
    }
  }, [step, formData.uploads]);

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <Step0Branding
            vendor={vendor}
            event={event}
            app={app}
            setCanProceed={setCanProceed}
          />
        );
      case 1:
        return (
          <Step1Tags
            formData={formData}
            setFormData={setFormData}
            setCanProceed={setCanProceed}
          />
        );
      case 2:
        return <Step2Download setCanProceed={setCanProceed} />;
      case 3:
        return (
          <Step3Upload
            vendor={vendor}
            event={event}
            app={app}
            formData={formData}
            setFormData={setFormData}
            setCanProceed={setCanProceed}
          />
        );
      case 4:
        return (
          <Step4ChooseSpot
            vendor={vendor}
            event={event}
            app={app}
            formData={formData}
            setFormData={setFormData}
            onNext={nextStep}
          />
        );
      case 5:
        return (
          <Step5Confirm
            vendor={vendor}
            event={event}
            app={app}
            formData={formData}
            setCanProceed={setCanProceed}
            onComplete={onComplete}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="vo-page">
      <div className="vo-container">

        {/* ── Header ── */}
        <div className="vo-header">
          <h2 className="vo-title">Vendor Onboarding</h2>
          <p className="vo-subtitle">{event?.name}</p>
        </div>

        {/* ── Step indicators ── */}
        <div className="vo-steps">
          {STEP_LABELS.map((label, i) => {
            const state = i < step ? "done" : i === step ? "active" : "pending";
            return (
              <div key={i} className={`vo-step-indicator vo-step-indicator--${state}`}>
                <div className="vo-step-bubble">
                  {i < step ? "✓" : i + 1}
                </div>
                <span className="vo-step-label">{label}</span>
                {i < STEP_LABELS.length - 1 && <div className="vo-step-connector" />}
              </div>
            );
          })}
        </div>

        {/* ── Progress bar ── */}
        <div className="vo-progress">
          <div
            className="vo-progress-fill"
            style={{ width: `${(step / (TOTAL_STEPS - 1)) * 100}%` }}
          />
        </div>

        {/* ── Step content ── */}
        <div className="vo-step-content">
          {renderStep()}
        </div>

        {/* ── Navigation ── */}
        <div className="vo-controls">
          {step > 0 ? (
            <button className="vo-btn vo-btn--back" onClick={prevStep}>
              ← Back
            </button>
          ) : (
            <div />
          )}

          {step < TOTAL_STEPS - 1 && (
            <button
              className={`vo-btn vo-btn--next ${!canProceed ? "vo-btn--disabled" : ""}`}
              onClick={nextStep}
              disabled={!canProceed}
            >
              Next →
            </button>
          )}
        </div>

      </div>
    </div>
  );
}